// noinspection JSUnusedGlobalSymbols

import React, { ReactElement, useMemo } from 'react';
import { DivButton } from './DivButton';
import { MoreIcon } from '../../assets/icons';
import { Dropdown } from 'react-bootstrap';
import { TextAndIcon } from './TextAndIcon';
import Dropzone, { FileRejection } from 'react-dropzone';

const GrowegyToggle = (dataTest: string, customButton?: () => ReactElement) =>
  React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(({ onClick }, ref) => (
    <div
      data-test={dataTest}
      // className="more-menu__menu-button"
      role="button"
      ref={ref}
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick && onClick(e);
      }}
      style={{ height: 'inherit' }}
    >
      {!!customButton ? customButton() : <DivButton icon={<MoreIcon />} />}
    </div>
  ));

type BaseMenuItem = {
  type: 'standard' | 'upload' | 'divider' | 'custom';
  isHidden?: boolean;
  dataTest?: string;
};

export type StandardMoreMenuItem = BaseMenuItem & {
  text: string;
  icon?: ReactElement;
};

export type ActionMenuItem = StandardMoreMenuItem & {
  type: 'standard';
  onClick: () => void;
};

export type UploadMenuItem = StandardMoreMenuItem & {
  type: 'upload';
  accept: string;
  onClick: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => void;
};

export type DividerMenuItem = BaseMenuItem & {
  type: 'divider';
};

export type CustomMenuItem = BaseMenuItem & {
  type: 'custom';
  component: () => ReactElement;
};

export const GrowegyMenu = (props: {
  menuItems: (ActionMenuItem | UploadMenuItem | DividerMenuItem | CustomMenuItem)[];
  menuButtonDataTest: string;
  containerDataTest?: string;
  containerClassName?: string;
  menuDataTest?: string;
  customToggle?: () => ReactElement;
  drop?: 'up' | 'left' | 'right' | 'down';
  positionStrategy?: 'absolute' | 'fixed';
}) => {
  const {
    menuItems,
    menuButtonDataTest,
    containerDataTest,
    containerClassName,
    menuDataTest,
    customToggle,
    drop,
    positionStrategy,
  } = props;
  const toggle = useMemo(
    () => GrowegyToggle(menuButtonDataTest, customToggle),
    [customToggle, menuButtonDataTest]
  );
  return (
    <Dropdown
      drop={drop ?? 'down'}
      alignRight={false}
      data-test={containerDataTest}
      className={containerClassName}
    >
      <Dropdown.Toggle as={toggle} className="d-flex align-items-center" />
      <Dropdown.Menu
        title=""
        data-test={menuDataTest}
        popperConfig={{ strategy: positionStrategy ?? 'absolute' }}
      >
        {menuItems
          .filter((x) => !x.isHidden)
          .map((x) => {
            switch (x.type) {
              case 'standard':
                return (
                  <Dropdown.Item
                    key={x.dataTest ?? x.text}
                    onClick={(e) => {
                      e.stopPropagation();
                      x.onClick();
                    }}
                    data-test={x.dataTest}
                  >
                    <TextAndIcon text={x.text} icon={x.icon} />
                  </Dropdown.Item>
                );
              case 'upload':
                return (
                  <Dropzone
                    key={x.dataTest ?? x.text}
                    onDrop={(acceptedFiles, rejectedFiles) =>
                      x.onClick(acceptedFiles, rejectedFiles)
                    }
                    accept={x.accept}
                    multiple={false}
                    noClick={true}
                    noKeyboard={true}
                  >
                    {({ getRootProps, getInputProps, open }) => (
                      <div {...getRootProps({ className: 'dropzone' })}>
                        <input {...getInputProps()} />
                        <Dropdown.Item onClick={open} data-test={x.dataTest}>
                          {x.text}
                        </Dropdown.Item>
                      </div>
                    )}
                  </Dropzone>
                );
              case 'divider':
                return <Dropdown.Divider key={x.dataTest} />;
              case 'custom':
                return (
                  <div className="dropdown-item--non-active" key={x.dataTest}>
                    {x.component()}
                  </div>
                );
              default: {
                return <></>;
              }
            }
          })}
      </Dropdown.Menu>
    </Dropdown>
  );
};
